import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import apiService from '../services/api';
import { Backup, BackupConfiguration } from '../types/api';
import Logger from '../utils/logger';
import BackupConfigModal from './Backup/BackupConfigModal';
import BackupStatusBadge from './Backup/BackupStatusBadge';
import WalInfoBadge from './Backup/WalInfoBadge';
import { SuccessMessages, ErrorMessages, getApiErrorMessage } from '../constants/messages';
import { useTranslation } from 'react-i18next';

interface BackupsSectionProps {
  deploymentId: string;
}

const BackupsSection: React.FC<BackupsSectionProps> = ({ deploymentId }) => {
  const { t } = (useTranslation as any)('backups');
  const [backups, setBackups] = useState<Backup[]>([]);
  const [config, setConfig] = useState<BackupConfiguration | null>(null);
  const [loading, setLoading] = useState(true);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [deletingBackupId, setDeletingBackupId] = useState<string | null>(null);

  useEffect(() => {
    loadBackups();
    loadConfig();
  }, [deploymentId]);

  const loadBackups = async () => {
    try {
      setLoading(true);
      const response = await apiService.listBackups(deploymentId, 1, 10);
      setBackups(response.backups);
    } catch (error: unknown) {
      Logger.error('Error loading backups:', error);
      toast.error(ErrorMessages.BACKUP_LOAD_FAILED);
    } finally {
      setLoading(false);
    }
  };

  const loadConfig = async () => {
    try {
      const configuration = await apiService.getBackupConfiguration(deploymentId);
      setConfig(configuration);
    } catch (error: unknown) {
      Logger.error('Error loading backup config:', error);
      // Config n'existe peut-être pas encore, c'est normal
      setConfig(null);
    }
  };

  const handleManualBackup = async () => {
    try {
      setCreatingBackup(true);
      await apiService.createOnDemandBackup(deploymentId);
      toast.success(SuccessMessages.BACKUP_CREATED);
      loadBackups();
    } catch (error: unknown) {
      Logger.error('Error creating manual backup:', error);
      toast.error(getApiErrorMessage(error));
    } finally {
      setCreatingBackup(false);
    }
  };

  const handleConfigured = () => {
    loadConfig();
    loadBackups();
  };

  const handleDeleteBackup = async (backupId: string) => {
    if (!window.confirm(t('section.deleteConfirm') as string)) {
      return;
    }

    try {
      setDeletingBackupId(backupId);
      await apiService.deleteBackup(deploymentId, backupId);
      toast.success(SuccessMessages.BACKUP_DELETED);
      loadBackups();
    } catch (error: unknown) {
      Logger.error('Error deleting backup:', error);
      toast.error(getApiErrorMessage(error));
    } finally {
      setDeletingBackupId(null);
    }
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getCronDescription = (cron: string): string => {
    const descriptions: { [key: string]: string } = {
      '0 2 * * *': 'Quotidien à 2h00',
      '0 */12 * * *': 'Toutes les 12 heures',
      '0 */6 * * *': 'Toutes les 6 heures',
      '0 2 * * 0': 'Hebdomadaire (Dimanche à 2h00)',
    };
    return descriptions[cron] || cron;
  };

  return (
    <div className="bg-surface-2 overflow-hidden shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg leading-6 font-medium text-text-primary">
            {t('section.title')}
          </h3>
          <div className="flex space-x-2">
            <button
              onClick={handleManualBackup}
              disabled={creatingBackup}
              className="inline-flex items-center px-3 py-2 border border-border-1 text-sm leading-4 font-medium rounded-md text-text-secondary bg-surface-3 hover:bg-surface-4 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 disabled:opacity-50"
            >
              {creatingBackup ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-text-secondary mr-2"></div>
                  {t('section.creating')}
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Backup Manuel
                </>
              )}
            </button>
            <button
              onClick={() => setShowConfigModal(true)}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-accent-600 hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {config ? t('section.modifyConfig') as string : t('section.configureBtn') as string}
            </button>
          </div>
        </div>

        {/* Configuration actuelle */}
        {config && (
          <div className="mb-4 p-4 bg-surface-0 rounded-lg border border-border-1">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <span className="text-xs text-text-tertiary">Statut</span>
                <div className="mt-1">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    config.enabled
                      ? 'bg-green-500/12 text-green-400'
                      : 'bg-surface-3 text-text-secondary'
                  }`}>
                    {config.enabled ? '✓ Activé' : '✗ Désactivé'}
                  </span>
                </div>
              </div>
              <div>
                <span className="text-xs text-text-tertiary">Planification</span>
                <p className="mt-1 text-sm text-text-primary">
                  {getCronDescription(config.schedule)}
                </p>
              </div>
              <div>
                <span className="text-xs text-text-tertiary">Rétention</span>
                <p className="mt-1 text-sm text-text-primary">
                  {config.retention_policy}
                </p>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-500"></div>
          </div>
        ) : backups.length === 0 ? (
          <div className="text-center py-8">
            <svg className="mx-auto h-12 w-12 text-text-quaternary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
            </svg>
            <p className="mt-2 text-sm text-text-tertiary">{t('section.noBackups')}</p>
            <p className="text-xs text-text-quaternary mt-1">
              {config?.enabled
                ? 'Les backups automatiques seront créés selon la planification configurée'
                : 'Configurez les backups automatiques ou créez un backup manuel'
              }
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border-1">
              <thead className="bg-surface-0">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                    Nom
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                    Taille
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                    WAL
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-text-tertiary uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-surface-2 divide-y divide-border-1">
                {backups.map((backup) => (
                  <tr key={backup.id} className="hover:bg-surface-0">
                    <td className="px-6 py-4 text-sm font-medium text-text-primary">
                      <div className="max-w-xs truncate" title={backup.backup_name}>
                        {backup.backup_name}
                      </div>
                      {backup.id && (
                        <div className="text-xs text-text-quaternary">
                          ID: {backup.id.slice(0, 8)}...
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <BackupStatusBadge status={backup.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text-tertiary">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        backup.backup_type === 'scheduled'
                          ? 'bg-blue-500/12 text-blue-400'
                          : 'bg-green-500/12 text-green-400'
                      }`}>
                        {backup.backup_type === 'scheduled' ? t('section.automatic') as string : t('section.manual') as string}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text-tertiary">
                      {new Date(backup.started_at).toLocaleString('fr-FR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text-tertiary">
                      {backup.size_bytes ? formatBytes(backup.size_bytes) : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <WalInfoBadge backup={backup} mode="compact" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-3">
                        <Link
                          to={`/deployments/${deploymentId}/backups`}
                          className="text-accent-400 hover:text-accent-300"
                        >
                          Voir les détails
                        </Link>
                        <button
                          onClick={() => handleDeleteBackup(backup.id)}
                          disabled={backup.status !== 'completed' || deletingBackupId === backup.id}
                          className="text-red-400 hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {deletingBackupId === backup.id ? t('section.deleting') as string : t('common:delete') as string}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de configuration */}
      <BackupConfigModal
        isOpen={showConfigModal}
        deploymentId={deploymentId}
        onClose={() => setShowConfigModal(false)}
        onConfigured={handleConfigured}
        currentConfig={config}
      />
    </div>
  );
};

export default BackupsSection;
